import { useLayoutEffect } from 'react';

/** Own only the authenticated shell geometry. React Aria owns its overlay variables. */
export function useAppViewport(enabled: boolean) {
  useLayoutEffect(() => {
    if (!enabled) return;
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const properties = ['--app-viewport-height', '--app-viewport-top'] as const;
    const previous = properties.map((name) => ({
      name, value: root.style.getPropertyValue(name), priority: root.style.getPropertyPriority(name),
    }));
    const marker = root.getAttribute('data-app-viewport');
    root.setAttribute('data-app-viewport', '');
    let frame = 0;
    let settleTimer = 0;

    const measure = () => {
      frame = 0;
      // Pinch is an intentional magnification, not a smaller layout. Leave width,
      // height and pan alone until the user returns to the unzoomed viewport.
      if (viewport && Math.abs(viewport.scale - 1) > 0.01) return;
      const height = viewport?.height ?? window.innerHeight;
      const top = viewport?.offsetTop ?? 0;
      // Ignore transient zero/invalid geometry while Safari changes orientation.
      if (!Number.isFinite(height) || height <= 0 || !Number.isFinite(top)) return;
      root.style.setProperty('--app-viewport-height', `${height}px`);
      root.style.setProperty('--app-viewport-top', `${Math.max(0, top)}px`);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    const settle = () => {
      schedule();
      window.clearTimeout(settleTimer);
      // Some Safari chrome/keyboard transitions publish their final geometry late.
      // One bounded follow-up also covers focus changing between nested overlays.
      settleTimer = window.setTimeout(schedule, 350);
    };
    measure();
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    window.addEventListener('resize', settle);
    window.addEventListener('orientationchange', settle);
    document.addEventListener('focusin', settle);
    document.addEventListener('focusout', settle);
    return () => {
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', settle);
      window.removeEventListener('orientationchange', settle);
      document.removeEventListener('focusin', settle);
      document.removeEventListener('focusout', settle);
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
      for (const { name, value, priority } of previous) {
        if (value) root.style.setProperty(name, value, priority);
        else root.style.removeProperty(name);
      }
      if (marker === null) root.removeAttribute('data-app-viewport');
      else root.setAttribute('data-app-viewport', marker);
    };
  }, [enabled]);
}
